<!-- Main Layout -->
<div class="min-h-screen bg-gray-50">
  <!-- Navbar -->
  <app-navbar
    [currentUser]="currentUser()?.first_name + ' ' + currentUser()?.last_name"
    [userRole]="getUserRole()"
  ></app-navbar>

  <!-- Content Area -->
  <div class="flex flex-col lg:flex-row min-h-[calc(100vh-64px)] pt-16">
    <!-- Admin Sidebar -->
    <app-sidebar
      [selectedMenu]="selectedMenu()"
      [userRole]="getUserRole()"
      (menuSelect)="onMenuSelect($event)"
      class="sidebar-adaptive"
    >
    </app-sidebar>

    <!-- Main Content -->
    <div class="flex-1 p-3 sm:p-4 lg:p-6 main-content transition-all duration-300">
      <!-- Page Header -->
      <app-page-header [showSearchBar]="false" [showFilter]="true" [showGreeting]="true"></app-page-header>

      <!-- Stats Cards -->
      <div class="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <!-- Total Appointments -->
        <div class="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
          <div class="flex items-center">
            <div class="flex-shrink-0">
              <div class="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                <svg class="w-6 h-6 text-primary" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fill-rule="evenodd"
                    d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z"
                    clip-rule="evenodd"
                  ></path>
                </svg>
              </div>
            </div>
            <div class="ml-4">
              <p class="text-sm font-medium text-gray-600">
                {{ translationService.translate('admin.dashboard.totalAppointments') }}
              </p>
              <p class="text-3xl font-bold text-gray-900">{{ totalAppointments() }}</p>
            </div>
          </div>
        </div>

        <!-- Total Patients -->
        <div class="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
          <div class="flex items-center">
            <div class="flex-shrink-0">
              <div class="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                <svg class="w-6 h-6 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fill-rule="evenodd"
                    d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                    clip-rule="evenodd"
                  ></path>
                </svg>
              </div>
            </div>
            <div class="ml-4">
              <p class="text-sm font-medium text-gray-600">
                {{ translationService.translate('admin.dashboard.totalPatients') }}
              </p>
              <p class="text-3xl font-bold text-gray-900">{{ totalPatients() }}</p>
            </div>
          </div>
        </div>

        <!-- Active Medics -->
        <div class="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
          <div class="flex items-center">
            <div class="flex-shrink-0">
              <div class="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <svg class="w-6 h-6 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3z"
                  ></path>
                </svg>
              </div>
            </div>
            <div class="ml-4">
              <p class="text-sm font-medium text-gray-600">
                {{ translationService.translate('admin.dashboard.activeMedics') }}
              </p>
              <p class="text-3xl font-bold text-gray-900">{{ activeMedics() }}</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Charts Section -->
      <div class="grid grid-cols-1 xl:grid-cols-2 gap-6 mb-8">
        <!-- Patients Chart -->
        <div class="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
          <div class="flex items-center justify-between mb-4">
            <h3 class="text-lg font-semibold text-gray-900">
              {{ translationService.translate('admin.dashboard.patients') }}
            </h3>
            <select
              class="text-sm border border-gray-300 rounded-md px-3 py-1 focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option>{{ translationService.translate('admin.dashboard.thisWeek') }}</option>
              <option>{{ translationService.translate('admin.dashboard.thisMonth') }}</option>
              <option>{{ translationService.translate('admin.dashboard.thisYear') }}</option>
            </select>
          </div>

          <!-- Chart Area -->
          <div class="h-64 relative">
            <svg class="w-full h-full" viewBox="0 0 400 200">
              <!-- Grid lines -->
              <defs>
                <pattern id="grid" width="40" height="25" patternUnits="userSpaceOnUse">
                  <path d="M 40 0 L 0 0 0 25" fill="none" stroke="#f1f5f9" stroke-width="1" />
                </pattern>
                <!-- Gradient for area fill -->
                <linearGradient id="areaGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" style="stop-color: rgb(var(--primary)); stop-opacity: 0.3" />
                  <stop offset="100%" style="stop-color: rgb(var(--primary)); stop-opacity: 0.05" />
                </linearGradient>
              </defs>
              <rect width="100%" height="100%" fill="url(#grid)" />

              <!-- Y-axis labels -->
              <g class="text-xs fill-gray-500">
                <text x="10" y="25">2000</text>
                <text x="10" y="50">1500</text>
                <text x="10" y="75">1000</text>
                <text x="10" y="100">500</text>
                <text x="10" y="125">200</text>
                <text x="10" y="150">100</text>
                <text x="10" y="175">0</text>
              </g>

              <!-- Area fill -->
              <polygon
                fill="url(#areaGradient)"
                points="50,120 100,110 150,125 200,95 250,85 300,90 350,100 350,175 50,175"
              />

              <!-- Chart line -->
              <polyline
                fill="none"
                stroke="rgb(var(--primary))"
                stroke-width="3"
                points="50,120 100,110 150,125 200,95 250,85 300,90 350,100"
              />

              <!-- Data points -->
              @for (point of patientData; track point.day; let i = $index) {
                <circle
                  [attr.cx]="50 + i * 50"
                  [attr.cy]="200 - (point.value / getChartMaxValue()) * 150 - 25"
                  r="5"
                  fill="rgb(var(--primary))"
                  stroke="white"
                  stroke-width="2"
                />
              }

              <!-- X-axis labels -->
              <g class="text-xs fill-gray-500">
                @for (point of patientData; track point.day; let i = $index) {
                  <text [attr.x]="45 + i * 50" y="195">
                    {{ point.day }}
                  </text>
                }
              </g>
            </svg>

            <!-- Current value indicator -->
            <div class="absolute top-4 right-4 bg-gray-900 text-white px-2 py-1 rounded text-sm">424</div>
          </div>
        </div>

        <!-- Traffic by Location -->
        <div class="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
          <h3 class="text-lg font-semibold text-gray-900 mb-4">
            {{ translationService.translate('admin.dashboard.trafficByLocation') }}
          </h3>

          <!-- Donut Chart -->
          <div class="flex items-center justify-center mb-6">
            <div class="relative w-48 h-48">
              <svg class="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
                <!-- Background circle -->
                <circle cx="50" cy="50" r="35" fill="none" stroke="#f1f5f9" stroke-width="8" />

                <!-- In-patients (63%) -->
                <circle
                  cx="50"
                  cy="50"
                  r="35"
                  fill="none"
                  stroke="rgb(var(--primary))"
                  stroke-width="8"
                  stroke-dasharray="138.6 360"
                  stroke-dashoffset="0"
                />

                <!-- Out-patients (24%) -->
                <circle
                  cx="50"
                  cy="50"
                  r="35"
                  fill="none"
                  stroke="#64748B"
                  stroke-width="8"
                  stroke-dasharray="52.8 360"
                  stroke-dashoffset="-138.6"
                />

                <!-- Other (13%) -->
                <circle
                  cx="50"
                  cy="50"
                  r="35"
                  fill="none"
                  stroke="#94A3B8"
                  stroke-width="8"
                  stroke-dasharray="28.6 360"
                  stroke-dashoffset="-191.4"
                />
              </svg>
            </div>
          </div>

          <!-- Legend -->
          <div class="space-y-3">
            @for (item of trafficData; track item.location) {
              <div class="flex items-center justify-between">
                <div class="flex items-center">
                  <div class="w-3 h-3 rounded-full mr-3" [style.background-color]="item.color"></div>
                  <span class="text-sm font-medium text-gray-900">{{ item.location }}</span>
                </div>
                <span class="text-sm font-semibold text-gray-900">{{ item.percentage }}%</span>
              </div>
            }
          </div>
        </div>
      </div>

      <!-- Recent Activities -->
      <div class="bg-white rounded-lg shadow-sm border border-gray-200">
        <div class="px-6 py-4 border-b border-gray-200">
          <h3 class="text-lg font-semibold text-gray-900">Recent Activities</h3>
        </div>

        <div class="overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">S/N</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Activity</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              @for (activity of recentActivities(); track activity.id) {
                <tr class="hover:bg-gray-50">
                  <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {{ activity.id }}
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {{ activity.name }}
                  </td>
                  <td class="px-6 py-4 text-sm text-gray-900">
                    {{ activity.activity }}
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {{ activity.date }}
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <button class="text-gray-400 hover:text-gray-600">
                      <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path
                          d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z"
                        ></path>
                      </svg>
                    </button>
                  </td>
                </tr>
              } @empty {
                <tr>
                  <td colspan="5" class="px-6 py-8 text-center text-sm text-gray-500">No activities recorded yet.</td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</div>

<!-- Chat Component -->
<app-chat [isOpen]="isChatOpen()" (closeChat)="closeChat()"></app-chat>
